import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import Sidebar from "../components/Sidebar";
import SnippetEditor from "../components/SnippetEditor";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";

const EditSnippet = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();
  const [snippet, setSnippet] = useState();
  const [error, setError] = useState(null);
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);

  const { id } = useParams();

  useEffect(() => {
    // Fetch snippet from backend
    // console.log("id", id);
    const fetchSnippet = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/snippets/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        const data = await response.json();

        if (data.message === "Invalid token") {
          // Remove user from local storage
          dispatch(logout());
          return;
        }

        setSnippet(data.snippet);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching snippets:", error);
        setError("Failed to load your snippets. Please try again later.");
        setLoading(false);
      }
    };

    fetchSnippet();
  }, [user.token, id, dispatch]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (snippetData) => {
    // console.log("snippetData", snippetData);
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/edit-snippet/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify(snippetData),
        }
      );

      const data = await response.json();

      if (data.message === "Invalid token") {
        // Remove user from local storage
        dispatch(logout());
        return;
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Snippet saved successfully!",
        severity: "success",
      });

      // Wait for snackbar to be visible before redirecting
      setTimeout(() => {
        navigate("/my-snippets");
      }, 1500);
    } catch (error) {
      console.error("Error saving snippet:", error.message);
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [darkMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        ) : (
          <div className="add-snippet-container">
            <Stack direction="row" spacing={2}>
              <Sidebar />
              <SnippetEditor
                handleSubmit={handleSubmit}
                title="Edit Snippet"
                loading={loading}
                snippet={snippet}
              />
            </Stack>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                variant="filled"
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </div>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default EditSnippet;
